import React, { useState } from 'react';
import { useMultiChain } from '../contexts/MultiChainProvider';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Wallet, LogOut, Copy, Check } from 'lucide-react';
import EVMWalletConnector from './EVMWalletConnector';

const MultiChainWalletButton = () => {
  const { 
    selectedChain, 
    EVM_CHAINS, 
    selectedEVMChain,
    evmAccount,
    evmBalance,
    disconnectEVMWallet
  } = useMultiChain();
  const { connected, publicKey, disconnect } = useWallet();
  const [showDropdown, setShowDropdown] = useState(false);
  const [copied, setCopied] = useState(false);

  const walletAddress = selectedChain === 'evm' ? evmAccount : publicKey?.toString();
  const isConnected = selectedChain === 'evm' ? !!evmAccount : connected;

  const handleDisconnect = () => {
    if (selectedChain === 'evm') {
      disconnectEVMWallet();
    } else {
      disconnect();
    }
    setShowDropdown(false);
  };

  const copyAddress = async () => {
    if (walletAddress) {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getChainInfo = () => {
    if (selectedChain === 'evm') {
      const chain = EVM_CHAINS[selectedEVMChain];
      return {
        name: chain?.name || 'Ethereum',
        symbol: chain?.symbol || 'ETH',
        color: 'text-blue-400',
      };
    }
    return {
      name: 'Solana',
      symbol: 'SOL',
      color: 'text-green-400',
    };
  };

  const chainInfo = getChainInfo();

  if (isConnected) {
    return (
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center space-x-2 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base min-h-[44px] sm:min-h-[48px]"
        >
          <Wallet className="w-4 h-4" />
          <span className="hidden sm:block">
            {walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}
          </span>
          <span className="sm:hidden">Connected</span>
        </button>

        {showDropdown && (
          <div className="absolute top-full right-0 mt-2 w-72 sm:w-80 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-[9999]">
            <div className="p-3 sm:p-4">
              {/* Chain Info */}
              <div className="flex items-center justify-between mb-3 sm:mb-4 pb-3 border-b border-gray-600">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${chainInfo.color.replace('text-', 'bg-')}`}></div>
                  <span className="text-white font-medium text-sm sm:text-base">{chainInfo.name}</span>
                </div>
                <span className="text-gray-400 text-xs sm:text-sm">{chainInfo.symbol}</span>
              </div>

              {/* Balance (for EVM) */}
              {selectedChain === 'evm' && evmBalance && (
                <div className="mb-3 sm:mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs sm:text-sm">Balance</span>
                  </div>
                  <div className="mt-1 p-2 sm:p-3 bg-gray-700/50 rounded text-xs sm:text-sm font-mono text-gray-300">
                    {parseFloat(evmBalance).toFixed(4)} {chainInfo.symbol}
                  </div>
                </div>
              )}

              {/* Address */}
              <div className="mb-3 sm:mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs sm:text-sm">Address</span>
                  <button
                    onClick={copyAddress}
                    className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors duration-300 p-1 rounded hover:bg-gray-700/50 min-h-[28px] min-w-[28px]"
                  >
                    {copied ? (
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                    ) : (
                      <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
                    )}
                  </button>
                </div>
                <div className="mt-1 p-2 sm:p-3 bg-gray-700/50 rounded text-xs sm:text-sm font-mono text-gray-300 break-all">
                  {walletAddress}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={handleDisconnect}
                  className="w-full flex items-center justify-center space-x-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg transition-all duration-300 text-sm sm:text-base min-h-[40px]"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Disconnect</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Not connected - show appropriate connect button
  if (selectedChain === 'evm') {
    return <EVMWalletConnector />;
  }

  // Solana - use the same wallet button with mobile responsive styling
  return (
    <WalletMultiButton className="!bg-gradient-to-r !from-green-500 !to-green-600 hover:!from-green-600 hover:!to-green-700 !text-white !px-4 sm:!px-6 !py-2.5 sm:!py-3 !rounded-xl !font-medium !transition-all !duration-300 !shadow-lg hover:!shadow-xl !transform hover:!scale-105 !text-sm sm:!text-base !min-h-[44px] sm:!min-h-[48px]" />
  );
};

export default MultiChainWalletButton; 